import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles } from "lucide-react";
import type { EnterpriseCustomField } from "@/hooks/useEnterpriseCustomFields";
import type { CustomFieldValues } from "./EnterpriseCustomFieldsStep";

interface EnterpriseCustomFieldsDisplayProps {
  fields: EnterpriseCustomField[];
  values: CustomFieldValues | undefined | null;
  /** Optional title override */
  title?: string;
}

/**
 * Renders configured enterprise custom field values inside a proposal results container.
 * Designed to live inside the html2canvas-captured DOM so values appear in the PDF.
 */
const EnterpriseCustomFieldsDisplay = ({
  fields,
  values,
  title = "Custom Information",
}: EnterpriseCustomFieldsDisplayProps) => {
  if (!fields || fields.length === 0) return null;

  // Filter to only fields that have a non-empty value
  const rendered = fields
    .map((f) => {
      const raw = values?.[f.field_key];
      let display: string | null = null;
      if (raw === undefined || raw === null || raw === "") return null;
      if (f.field_type === "checkbox") {
        display = raw ? "Yes" : "No";
      } else {
        display = String(raw);
      }
      return { field: f, display };
    })
    .filter((r): r is { field: EnterpriseCustomField; display: string } => r !== null);

  if (rendered.length === 0) return null;

  return (
    <Card className="border-border bg-card shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Sparkles className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {rendered.map(({ field, display }) => (
            <div key={field.id} className="space-y-0.5">
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {field.field_label}
              </dt>
              <dd className="text-sm text-foreground whitespace-pre-wrap break-words">{display}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

export default EnterpriseCustomFieldsDisplay;
